import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Fragment, type FC, useCallback, useEffect, useState } from "react";
import { Matcher } from "react-day-picker";

import { Side } from "../ui/CustomChervonIcon";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/core/generalUtils";
import { toValidDate } from "@/utils/datetime/dateTimeHelper";

type DateInputProps = {
  onChange: (selectedDate?: Date) => void;
  validateDate?: (date: Date) => boolean;
} & {
  value?: Date | string | null;
  defaultValue?: Date;
  dateLimit?: Date | string;
  className?: string;
  placeholder?: string;
  isDateTime?: boolean;
  showTimePicker?: boolean;
  side?: Side;
  required?: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  defaultCalendarOpen?: boolean;
  dateFormat?: string;
  matchers?: Matcher | Matcher[];
};

const DateInput: FC<DateInputProps> = (props) => {
  const {
    onChange,
    value,
    defaultValue,
    dateLimit,
    validateDate,
    className,
    placeholder,
    required,
    side,
    disabled,
    isReadOnly,
    dateFormat = "PPP",
    matchers,
    defaultCalendarOpen = false,
  } = props;

  const isControlled = "value" in props;
  const [localDate, setLocalDate] = useState<Date | undefined>(() =>
    toValidDate(defaultValue),
  );
  const date = isControlled ? toValidDate(value) : localDate;

  const [month, setMonth] = useState<Date | undefined>(
    toValidDate(isControlled ? value : defaultValue) || new Date(),
  );

  const ExtendedComponent = isReadOnly ? Fragment : PopoverTrigger;

  const handleChange = useCallback(
    (selectedDate?: Date) => {
      if (!isControlled) {
        setLocalDate(selectedDate);
      }
      onChange(selectedDate);
    },
    [onChange, isControlled],
  );

  useEffect(() => {
    if (!isControlled) {
      const parsed = toValidDate(defaultValue);
      setLocalDate(parsed);
      setMonth(parsed || new Date());
    }
  }, [defaultValue, isControlled, toValidDate]);

  useEffect(() => {
    if (isControlled) {
      const parsed = toValidDate(value);
      setMonth(parsed || new Date());
    }
  }, [value, isControlled, toValidDate]);

  return (
    <Popover defaultOpen={defaultCalendarOpen}>
      <ExtendedComponent {...(!isReadOnly && { asChild: true })}>
        <Button
          type='button'
          variant={"outline"}
          className={cn(
            "w-full pl-3 text-left font-normal flex items-center gap-2 justify-start",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className='opacity-50 size-4 mb-0.5' />
          {date ? (
            format(date, dateFormat)
          ) : (
            <span className='opacity-30'>{placeholder ?? "Pick a date"}</span>
          )}
        </Button>
      </ExtendedComponent>
      <PopoverContent className='p-1' side={side}>
        <Calendar
          mode='single'
          selected={date as Date}
          month={month}
          onMonthChange={setMonth}
          onSelect={(selectedDate: Date | undefined) =>
            handleChange(selectedDate)
          }
          required={required}
          disabled={
            matchers
              ? matchers
              : (calendarDate: Date): boolean => {
                  if (validateDate) {
                    return validateDate(calendarDate);
                  }

                  let limitDate: Date;

                  if (typeof dateLimit === "string") {
                    const parsed = new Date(dateLimit);
                    limitDate = isNaN(parsed.getTime())
                      ? new Date("1900-01-01")
                      : parsed;
                  } else {
                    limitDate = dateLimit ?? new Date("1900-01-01");
                  }

                  return calendarDate < limitDate;
                }
          }
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateInput;
